// Multi renderer — plan §6.3 + M5.5 custom input.
// Value shape: { optionIds: string[]; customInputs?: Record<optionId, string> }.

import { CUSTOM_INPUT_MAX_LENGTH } from "@mahir/shared";
import type { QuestionSnapshot } from "@mahir/shared";
import { FORM_FILL_COPY } from "../../../lib/form-fill-copy";

interface Props {
  question: QuestionSnapshot;
  value: { optionIds: string[]; customInputs?: Record<string, string> } | null;
  onChange: (v: { optionIds: string[]; customInputs?: Record<string, string> } | null) => void;
}

interface OptionItem {
  id: string;
  label: string;
  allowCustomInput?: boolean;
}

export function MultiRenderer({ question, value, onChange }: Props): JSX.Element {
  const opts = (question.options as { choices?: OptionItem[] } | null)?.choices ?? [];
  const selected = new Set(value?.optionIds ?? []);
  const customInputs = value?.customInputs ?? {};

  const toggle = (id: string): void => {
    const next = new Set(selected);
    const nextCustoms = { ...customInputs };
    if (next.has(id)) {
      next.delete(id);
      delete nextCustoms[id]; // seçim kalkarsa custom metin de silinir
    } else {
      next.add(id);
    }
    const payload: { optionIds: string[]; customInputs?: Record<string, string> } = {
      optionIds: Array.from(next),
    };
    if (Object.keys(nextCustoms).length > 0) payload.customInputs = nextCustoms;
    onChange(payload);
  };

  const setCustom = (id: string, text: string): void => {
    const nextCustoms = { ...customInputs };
    if (text.length > 0) nextCustoms[id] = text;
    else delete nextCustoms[id];
    const payload: { optionIds: string[]; customInputs?: Record<string, string> } = {
      optionIds: Array.from(selected),
    };
    if (Object.keys(nextCustoms).length > 0) payload.customInputs = nextCustoms;
    onChange(payload);
  };

  return (
    <div className="flex flex-col gap-2">
      {opts.length === 0 ? (
        <div
          className="text-xs px-2 py-1 rounded"
          style={{ background: "var(--color-bg)", color: "var(--color-text-3)" }}
        >
          Bu soru için seçenek tanımlanmamış.
        </div>
      ) : (
        opts.map((o) => {
          const checked = selected.has(o.id);
          return (
            <div key={o.id}>
              <label
                className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
                style={{
                  background: checked ? "var(--color-bg)" : "var(--color-surface)",
                  border: "1px solid",
                  borderColor: checked ? "var(--color-primary)" : "var(--color-border)",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(o.id)}
                  style={{ accentColor: "var(--color-primary)" }}
                />
                <span
                  style={{
                    color: checked ? "var(--color-primary)" : "var(--color-text)",
                    fontSize: "0.88rem",
                    fontWeight: checked ? 600 : 400,
                  }}
                >
                  {o.label}
                </span>
                {o.allowCustomInput && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.65rem",
                      color: "var(--color-text-3)",
                    }}
                  >
                    {FORM_FILL_COPY.customInputBadge}
                  </span>
                )}
              </label>
              {checked && o.allowCustomInput && (
                <textarea
                  value={customInputs[o.id] ?? ""}
                  onChange={(e) => setCustom(o.id, e.target.value)}
                  maxLength={CUSTOM_INPUT_MAX_LENGTH}
                  rows={2}
                  placeholder={`${o.label} — lütfen açıklayınız…`}
                  className="mt-1 ml-8 w-full rounded-lg px-3 py-2 text-sm resize-none"
                  style={{
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-border)",
                    color: "var(--color-text)",
                    fontFamily: "var(--font-sans)",
                    width: "calc(100% - 2rem)",
                  }}
                />
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
